import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";

import { AuthedLayout } from "@/app/AuthedLayout";
import { RootLayout } from "@/app/RootLayout";
import { ActionPlansPage } from "@/features/action-plans";
import { BillingPage } from "@/features/billing";
import {
  CommunicationReviewPage,
  CommunicationsPage,
} from "@/features/communications";
import {
  ExpertAdminPage,
  ExpertDashboardPage,
  ExpertObjectivesPage,
  ExpertPage,
  ExpertProfilePage,
  ExpertRegisterPage,
} from "@/features/expert";
import {
  FormationGroupCreatePage,
  FormationGroupDetailPage,
  FormationGroupEditPage,
  FormationGroupParticipantPage,
  FormationGroupParticipantProfilePage,
  FormationGroupsPage,
} from "@/features/formation-groups";
import { ConsentsPage, PartnersPage } from "@/features/partners";
import { CataloguePage } from "@/features/catalogue";
import { EnterpriseProfilePage, QuestionnairePage } from "@/features/questionnaire";
import { ReportsPage } from "@/features/reports";
import { SettingsPage } from "@/features/settings";
import { ToolsAdminPage, ToolsPage } from "@/features/tools";
import { FinancialAidsAdminPage, FinancialAidsPage } from "@/features/financial-aids";
import { EnrolmentPage, EnrolmentVerifyPage } from "@/features/enrolment";
import { AutosaveDemoPage } from "@/routes/autosave-demo";
import { ExpertInvitationPage } from "@/routes/expert-invitation";
import { ExpertSignupPage } from "@/routes/expert-signup";
import { DashboardPage } from "@/routes/dashboard";
import { ForgotPasswordPage } from "@/routes/forgot-password";
import { LoginPage } from "@/routes/login";
import { NotFound } from "@/routes/not-found";
import { ResetPasswordPage } from "@/routes/reset-password";
import { PartnerInvitationPage } from "@/routes/partner-invitation";
import { SignupPage } from "@/routes/signup";
import { StyleGuidePage } from "@/routes/style-guide";
import { VerifyEmailPage } from "@/routes/verify-email";

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFound,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/dashboard" });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: SignupPage,
  // token + email arrive from the invitation landing route (A-5): prefill the
  // email and claim the invitation on register.
  validateSearch: (
    search: Record<string, unknown>,
  ): { token?: string; email?: string } => ({
    token: typeof search.token === "string" ? search.token : undefined,
    email: typeof search.email === "string" ? search.email : undefined,
  }),
});

// Public partner/member invitation landing (`/invitation/$token`), outside the
// authed shell — an invited user has no session yet. Validates the token and
// routes into signup with the email prefilled (A-5).
const partnerInvitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invitation/$token",
  component: PartnerInvitationPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  component: ForgotPasswordPage,
});

// The public enrolment journey (OAC-464 part 2). Both sit outside the authed
// shell: the first is reached by a stranger with a code, and the second opens
// the session itself from the emailed link.
const enrolmentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/enrolment",
  component: EnrolmentPage,
});

const enrolmentVerifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/enrolment/verify/$payload",
  component: EnrolmentVerifyPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: ResetPasswordPage,
  // uid + token come from the emailed reset link.
  validateSearch: (
    search: Record<string, unknown>,
  ): { uid?: string; token?: string } => ({
    uid: typeof search.uid === "string" ? search.uid : undefined,
    token: typeof search.token === "string" ? search.token : undefined,
  }),
});

// Email-confirmation landing for SPA signups: the confirmation email links
// here with `?key=…`, and the page posts the key to the API verify endpoint.
// Outside the authed shell — the account isn't verified (or logged in) yet.
const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/verify-email",
  component: VerifyEmailPage,
  validateSearch: (search: Record<string, unknown>): { key?: string } => ({
    key: typeof search.key === "string" ? search.key : undefined,
  }),
});

// Public expert onboarding (invitation acceptance + account creation), outside
// the authed shell — an invited expert has no session yet.
const expertInvitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/experts/invitation/$token",
  component: ExpertInvitationPage,
});

const expertSignupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/experts/signup",
  component: ExpertSignupPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): { token?: string; email?: string } => ({
    token: typeof search.token === "string" ? search.token : undefined,
    email: typeof search.email === "string" ? search.email : undefined,
  }),
});

// Pathless layout route: authenticates once, then renders the app shell for all
// nested routes. Children use absolute paths.
const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "authed",
  component: AuthedLayout,
});

const dashboardRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/dashboard",
  component: DashboardPage,
});

const autosaveDemoRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/autosave-demo",
  component: AutosaveDemoPage,
});

const adminRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/admin",
  component: ReportsPage,
});

const partnerGroupRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/partner-group",
  component: PartnersPage,
});

const consentsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/consents",
  component: ConsentsPage,
  // `?next=` is where an existing member accepted an invitation from; the page
  // offers to go back there. Only an in-app path is kept.
  validateSearch: (search: Record<string, unknown>): { next?: string } => ({
    next:
      typeof search.next === "string" &&
      search.next.startsWith("/") &&
      !search.next.startsWith("//")
        ? search.next
        : undefined,
  }),
});

const expertRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/experts",
  component: ExpertPage,
  // `?action=<id>` deep-links the directory to a specific action's experts
  // (e.g. the "find experts" link on an action-plan action). `?fav=1` opens it
  // in favorites-only mode (the action row's favorites-heart entry point).
  validateSearch: (
    search: Record<string, unknown>,
  ): { action?: string; fav?: boolean } => ({
    action: typeof search.action === "string" ? search.action : undefined,
    fav: search.fav === true || search.fav === "1" || search.fav === "true"
      ? true
      : undefined,
  }),
});

const expertProfileRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/experts/profile/$id",
  component: ExpertProfilePage,
  // `?action=<id>` (from a directory card) enables the ♥ favorite toggle.
  validateSearch: (search: Record<string, unknown>): { action?: string } => ({
    action: typeof search.action === "string" ? search.action : undefined,
  }),
});

const expertDashboardRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/experts/dashboard",
  component: ExpertDashboardPage,
});

const expertRegisterRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/experts/register",
  component: ExpertRegisterPage,
});

const expertObjectivesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/experts/objectives",
  component: ExpertObjectivesPage,
});

const expertAdminRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/experts/admin",
  component: ExpertAdminPage,
  // The back-office lists experts and invitations in two tabs; `?tab=` keeps the
  // one you were on across a reload and makes it linkable (parity with the
  // Django list, which drives its tabs from the same parameter).
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab?: "experts" | "invitations" } => ({
    tab: search.tab === "invitations" ? "invitations" : undefined,
  }),
});

const toolsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/tools",
  component: ToolsPage,
  // `?action=<id>` deep-links the directory to a specific action's tools (the
  // "Outils" icon on an action-plan action). `?fav=1` opens favorites-only.
  validateSearch: (
    search: Record<string, unknown>,
  ): { action?: string; fav?: boolean } => ({
    action: typeof search.action === "string" ? search.action : undefined,
    fav:
      search.fav === true || search.fav === "1" || search.fav === "true"
        ? true
        : undefined,
  }),
});

const toolsAdminRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/tools/admin",
  component: ToolsAdminPage,
});

// Training groups (CQDD). Admin-gated at the page, and by
// `api.permissions.IsAdminUser` at every endpoint behind them.
const formationGroupsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/formation-groups",
  component: FormationGroupsPage,
});

const formationGroupCreateRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/formation-groups/new",
  component: FormationGroupCreatePage,
});

const formationGroupDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/formation-groups/$id",
  component: FormationGroupDetailPage,
});

const formationGroupEditRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/formation-groups/$id/edit",
  component: FormationGroupEditPage,
});

const formationGroupParticipantRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/formation-groups/$id/participants/$profileId",
  component: FormationGroupParticipantPage,
});

const formationGroupParticipantProfileRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/formation-groups/$id/participants/$profileId/profile",
  component: FormationGroupParticipantProfilePage,
});

// Questionnaire content (CQDD). Admin-gated at the page, and by
// `api.permissions.IsAdminUser` at every endpoint behind it.
const catalogueRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/catalogue",
  component: CataloguePage,
  validateSearch: (search: Record<string, unknown>): { tab?: string } => ({
    tab: typeof search.tab === "string" ? search.tab : undefined,
  }),
});

const financialAidsAdminRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/financial-aids/admin",
  component: FinancialAidsAdminPage,
});

const financialAidsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/financial-aids",
  component: FinancialAidsPage,
  // `?action=<id>` deep-links the directory to a specific action's aids (the
  // "Aides financières" icon on an action-plan action). `?fav=1` opens
  // favorites-only.
  validateSearch: (
    search: Record<string, unknown>,
  ): { action?: string; fav?: boolean } => ({
    action: typeof search.action === "string" ? search.action : undefined,
    fav:
      search.fav === true || search.fav === "1" || search.fav === "true"
        ? true
        : undefined,
  }),
});

// Wave 2 feature slices — stub pages pre-registered so worktrees only edit
// their own src/features/<slice>/** (see docs/react-parallel-plan.md).
const questionnaireRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/questionnaire",
  component: QuestionnairePage,
  // `?view=results|issues` deep-links into the diagnostic's results or
  // extra-issues surface (the dashboard action-hub shortcuts). Anything else
  // (incl. absent) lands on the default answering view. `?from=communications`
  // says the extra-issues surface was opened from the communication issue
  // step, so its Back returns there rather than to the dashboard.
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    view?: "results" | "issues";
    mode?: "browse" | "focus";
    from?: "communications";
  } => ({
    view:
      search.view === "results" || search.view === "issues"
        ? search.view
        : undefined,
    from: search.from === "communications" ? search.from : undefined,
    mode:
      search.mode === "focus" || search.mode === "browse"
        ? search.mode
        : undefined,
  }),
});

const actionPlansRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/action-plans",
  component: ActionPlansPage,
});

const communicationsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/communications",
  component: CommunicationsPage,
  // `?step=` opens the authoring wizard on a given step — what a Back from a
  // page reached out of that step returns to.
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    review?: string;
    step?: "profile" | "issues" | "actions" | "preview";
  } => ({
    review: typeof search.review === "string" ? search.review : undefined,
    step:
      search.step === "profile" ||
      search.step === "issues" ||
      search.step === "actions" ||
      search.step === "preview"
        ? search.step
        : undefined,
  }),
  // Preserve old `/communications?review=<id>` deep-links: the validator review
  // flow moved to its own route.
  beforeLoad: ({ search }) => {
    if (search.review) {
      throw redirect({
        to: "/communication-review",
        search: { review: search.review },
      });
    }
  },
});

// Validator communication review — in the header Management menu, gated to the
// `communications` capability (see Nav.tsx / RequireCapability in the page).
// `?review=<id>` still deep-links straight to a specific enterprise's review.
const communicationReviewRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/communication-review",
  component: CommunicationReviewPage,
  validateSearch: (search: Record<string, unknown>): { review?: string } => ({
    review: typeof search.review === "string" ? search.review : undefined,
  }),
});

// Standalone enterprise profile editor, reached from the header user menu.
// Reuses the questionnaire's ProfileEditor side-door.
const enterpriseProfileRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/enterprise-profile",
  component: EnterpriseProfilePage,
});

const billingRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/billing",
  component: BillingPage,
});

// Internal design reference for the SPA — the counterpart of the Django app's
// /guide-de-style/. Gated to the CQDD back-office inside the page, and absent
// from the nav on purpose: you reach it by typing the URL.
const styleGuideRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/style-guide",
  component: StyleGuidePage,
});

const settingsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/settings",
  component: SettingsPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  signupRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  enrolmentRoute,
  enrolmentVerifyRoute,
  verifyEmailRoute,
  partnerInvitationRoute,
  expertInvitationRoute,
  expertSignupRoute,
  authedRoute.addChildren([
    dashboardRoute,
    autosaveDemoRoute,
    adminRoute,
    partnerGroupRoute,
    questionnaireRoute,
    actionPlansRoute,
    communicationsRoute,
    communicationReviewRoute,
    enterpriseProfileRoute,
    billingRoute,
    consentsRoute,
    expertRoute,
    expertProfileRoute,
    expertDashboardRoute,
    expertRegisterRoute,
    expertObjectivesRoute,
    expertAdminRoute,
    toolsRoute,
    toolsAdminRoute,
    formationGroupsRoute,
    formationGroupCreateRoute,
    formationGroupDetailRoute,
    formationGroupEditRoute,
    formationGroupParticipantRoute,
    formationGroupParticipantProfileRoute,
    catalogueRoute,
    financialAidsAdminRoute,
    financialAidsRoute,
    styleGuideRoute,
    settingsRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultNotFoundComponent: NotFound,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
